import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { setItem } from 'utils/localStorage';

const useIdleTimeout = (timeoutDuration) => {
  const navigate = useNavigate();
  const timerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(30); // 30 seconds countdown

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
    setShowWarning(false);
    setCountdown(30);

    // Set warning popup to appear 30 seconds before logout
    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      // Start countdown timer
      countdownRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            setItem('access_token', null);
            navigate('/auth/sign-in');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, timeoutDuration - 30000);

    timerRef.current = setTimeout(() => {
      // This is a fallback in case countdown fails
      setItem('access_token', null);
      navigate('/auth/sign-in');
    }, timeoutDuration);
  }, [navigate, timeoutDuration]);

  const handleLogout = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
    setItem('access_token', null);
    navigate('/auth/sign-in');
  };

  const handleStayIn = () => {
    resetTimer();
  };

  useEffect(() => {
    // Set up event listeners for user activity
    window.addEventListener('mousemove', resetTimer);
    window.addEventListener('keydown', resetTimer);
    window.addEventListener('click', resetTimer);
    window.addEventListener('scroll', resetTimer);

    // Initialize timer
    resetTimer();

    return () => {
      // Clean up event listeners and timers on component unmount
      window.removeEventListener('mousemove', resetTimer);
      window.removeEventListener('keydown', resetTimer);
      window.removeEventListener('click', resetTimer);
      window.removeEventListener('scroll', resetTimer);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [resetTimer]);

  return (
    <>
      {showWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-navy-800 rounded-xl p-6 shadow-lg max-w-sm w-full">
            <p className="text-center text-navy-700 dark:text-white mb-4">
              You have been inactive for long and you are about to be logged out!
            </p>
            <p className="text-center text-navy-700 dark:text-white mb-4">
              Time remaining: {countdown} seconds
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={handleLogout}
                className="bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg text-base font-medium transition duration-200"
              >
                I'm Done!
              </button>
              <button
                onClick={handleStayIn}
                className="bg-brand-500 hover:bg-brand-600 text-white py-2 px-4 rounded-lg text-base font-medium transition duration-200"
              >
                Stay In!
              </button>
            </div>
          </div>
        </div>
      )}
      {resetTimer}
    </>
  );
};

export default useIdleTimeout;